// Deterministic placeholder pricing for rooms and services across all Xaluca hotels.
// All prices are public starting rates in MAD (Moroccan Dirham). Real figures should
// replace these values once provided by the brand. Agency pricing is intentionally
// not stored here — it is shown to guests as "On Request".

// Hotel tier (multiplier on base prices). Refuges/auberges trend lower, signature
// kasbahs sit mid-band, luxury bivouacs and suite-tent camps trend higher.
const HOTEL_TIER = {
  "kasbah-hotel-xaluca-arfoud": 1.10,
  "xaluca-hotel-spa-tombouctou-arfoud": 1.05,
  "hotel-xaluca-dades": 1.00,
  "hotel-xaluca-spa-aguelmame-sidi-ali": 1.00,
  "hotel-xaluca-midelt": 0.92,
  "xaluca-ait-ben-haddou": 0.95,
  "xaluca-ifess-fess-refuge": 0.70,
  "auberge-la-belle-etoile": 0.85,
  "bivouacs-xaluca": 1.30,
  "la-belle-etoile-bivouacs": 1.40,
  "kamkamia-bivouac": 1.25,
  "merdani-bivouac": 1.20,
};

// Base public rates per night in MAD (single occupancy, full board excluded).
// Keyword-based — first match wins, ordered from most specific to most generic.
const ROOM_BASE_MAD = [
  { match: /royal\s*suite|signature\s*suite/i, mad: 6800 },
  { match: /xaluca\s*suite|grand\s*suite|presidential/i, mad: 5400 },
  { match: /junior\s*suite/i, mad: 3400 },
  { match: /suite/i, mad: 4200 },
  { match: /bungalow/i, mad: 2800 },
  { match: /family|familiale|familiar/i, mad: 2400 },
  { match: /quadruple|cuádruple/i, mad: 2600 },
  { match: /triple/i, mad: 2100 },
  { match: /superior|supérieure/i, mad: 1900 },
  { match: /double/i, mad: 1700 },
  { match: /twin/i, mad: 1650 },
  { match: /standard|estándar/i, mad: 1400 },
  { match: /suite\s*tent|tente\s*suite/i, mad: 5200 },
  { match: /haima|tent|tente|camp/i, mad: 2400 },
  { match: /shared|partagée|compartida/i, mad: 850 },
];

// Service base price in MAD. `unit` describes how the rate is charged.
// Order matters — keep specific keywords above generic ones, and place
// "included" categories above bar/lounge to avoid confounds (e.g. pool desc
// mentioning a pool bar, sport desc mentioning "table tennis").
const SERVICE_BASE_MAD = [
  { match: /spa|hammam|wellness|massage|ritual|rituel/i, mad: 650, unit: "ritual" },
  { match: /boutique|atelier|shop/i, mad: 0, unit: "browse" },
  { match: /concierge|wi-?fi/i, mad: 0, unit: "included" },
  { match: /event|conference|évén|conférence|reunión|evento|salle\s*de\s*conf/i, mad: 4500, unit: "half_day_room" },
  { match: /transfer|airport|aéroport|navette/i, mad: 850, unit: "vehicle" },
  { match: /guide|excursion|trek/i, mad: 950, unit: "per_person" },
  { match: /sport|tennis|yoga|fitness|padel|football|bike|gym/i, mad: 220, unit: "session" },
  { match: /pool|piscine|piscina|swim/i, mad: 0, unit: "included" },
  { match: /restaurant|cuisine|dining|gastronom|food/i, mad: 350, unit: "set_menu" },
  { match: /bar|lounge/i, mad: 120, unit: "cocktail" },
];

const UNIT_LABELS = {
  ritual: { en: "/ ritual", fr: "/ rituel", es: "/ ritual" },
  set_menu: { en: "/ set menu", fr: "/ menu", es: "/ menú" },
  cocktail: { en: "/ cocktail", fr: "/ cocktail", es: "/ cóctel" },
  included: { en: "Included", fr: "Inclus", es: "Incluido" },
  half_day_room: { en: "/ half-day · room", fr: "/ demi-journée · salle", es: "/ media jornada · sala" },
  browse: { en: "Free to browse", fr: "Entrée libre", es: "Entrada libre" },
  session: { en: "/ session", fr: "/ séance", es: "/ sesión" },
  vehicle: { en: "/ vehicle · 4 pax", fr: "/ véhicule · 4 pers.", es: "/ vehículo · 4 pers." },
  per_person: { en: "/ person", fr: "/ personne", es: "/ persona" },
  per_night: { en: "/ night", fr: "/ nuit", es: "/ noche" },
};

const formatMAD = (value) =>
  new Intl.NumberFormat("fr-MA", { maximumFractionDigits: 0 }).format(value);

// Public formatter — returns "1,400 MAD" or null if value is not a number.
export const formatMADAmount = (value) =>
  (value == null || Number.isNaN(Number(value))) ? null : `${formatMAD(Number(value))} MAD`;

const pickName = (named) => {
  if (!named) return "";
  if (typeof named === "string") return named;
  return named.en || named.fr || Object.values(named)[0] || "";
};

const lookup = (label, table) => {
  for (const entry of table) {
    if (entry.match.test(label)) return entry;
  }
  return null;
};

// Public room price (MAD, /night). Returns null if we cannot infer.
// Honours CMS pricing overrides (room.pricing) when present — admins can
// set any subset of {room_only, breakfast, half_board, full_board} in the
// /admin/hotels editor and the website picks them up automatically.
//
// Per-person semantics:
//   - `room.pricing.price_per_person` (MAD/night/pax, room-only base)
//     is the manual field brand admins fill in the CMS. Board deltas
//     (breakfast +60 · half-board +200 · full-board +375 MAD / pax)
//     are added to derive the other three plans in per-person form.
//   - `room.pricing.occupancy` overrides the base occupancy used for
//     display. When missing, we fall back to the lower bound of the
//     localized `capacity` string ("2-4 guests" → 2).
//   - If `price_per_person` is missing, per-person plans are derived
//     by dividing the per-room plans by the occupancy — so nothing
//     blanks out during the manual data-entry period.
export const getRoomPricing = (room, hotelSlug) => {
  const label = pickName(room?.name) + " " + (room?.surface || "");
  const found = lookup(label, ROOM_BASE_MAD);
  const tier = HOTEL_TIER[hotelSlug] ?? 1.0;
  const baseRoomOnly = found ? Math.round((found.mad * tier) / 50) * 50 : null;

  // Deterministic add-ons (rounded to nearest 50 MAD) so every priced room
  // gets a full 4-plan grid out-of-the-box. Admins can override any value
  // individually via the CMS pricing fields.
  const deriveFromRoomOnly = (ro) => {
    if (ro == null) return null;
    return {
      room_only: ro,
      breakfast: Math.round((ro + 120) / 50) * 50,
      half_board: Math.round((ro + 400) / 50) * 50,
      full_board: Math.round((ro + 750) / 50) * 50,
    };
  };

  const derived = deriveFromRoomOnly(baseRoomOnly);

  // Merge CMS overrides on top of the derived baseline. Missing CMS values
  // fall back to the derived ones so admins only need to fill what they care
  // about. If no baseline exists, only the CMS-provided values are returned.
  const cms = (room && room.pricing && typeof room.pricing === "object") ? room.pricing : null;
  let plans = null;
  if (derived && cms) {
    plans = {
      room_only:  cms.room_only  != null ? Number(cms.room_only)  : derived.room_only,
      breakfast:  cms.breakfast  != null ? Number(cms.breakfast)  : derived.breakfast,
      half_board: cms.half_board != null ? Number(cms.half_board) : derived.half_board,
      full_board: cms.full_board != null ? Number(cms.full_board) : derived.full_board,
    };
  } else if (derived) {
    plans = derived;
  } else if (cms) {
    plans = {
      room_only:  cms.room_only  != null ? Number(cms.room_only)  : null,
      breakfast:  cms.breakfast  != null ? Number(cms.breakfast)  : null,
      half_board: cms.half_board != null ? Number(cms.half_board) : null,
      full_board: cms.full_board != null ? Number(cms.full_board) : null,
    };
  }

  if (!plans) return null;

  // "From" = lowest non-null plan
  const validValues = Object.values(plans).filter((v) => v != null && !Number.isNaN(v));
  const fromMAD = validValues.length ? Math.min(...validValues) : null;

  // ─── Per-person semantics ────────────────────────────────────────────
  // Occupancy — CMS override first, otherwise parse the localized capacity
  // string. Never falls below 1 to avoid divide-by-zero.
  const occOverride =
    cms && cms.occupancy != null && !Number.isNaN(Number(cms.occupancy))
      ? parseInt(cms.occupancy, 10)
      : null;
  const occFromCapacity = (() => {
    const cap = room?.capacity;
    const raw =
      (cap && typeof cap === "object" ? cap.en || cap.fr || cap.es : cap) || "";
    const m = String(raw).match(/(\d+)/);
    return m ? Math.max(1, parseInt(m[1], 10)) : 2;
  })();
  const occupancy = occOverride && occOverride > 0 ? occOverride : occFromCapacity;

  // Per-person base — CMS field wins, otherwise derive from room_only/occ.
  const pppBase =
    cms && cms.price_per_person != null && !Number.isNaN(Number(cms.price_per_person))
      ? Number(cms.price_per_person)
      : plans.room_only != null
      ? Math.round(Number(plans.room_only) / occupancy / 10) * 10
      : null;

  // Per-person deltas per plan (per pax per night, MAD).
  const PPP_DELTAS = { room_only: 0, breakfast: 60, half_board: 200, full_board: 375 };
  const pppPlans =
    pppBase == null
      ? null
      : {
          room_only:  Math.round((pppBase + PPP_DELTAS.room_only)  / 10) * 10,
          breakfast:  Math.round((pppBase + PPP_DELTAS.breakfast)  / 10) * 10,
          half_board: Math.round((pppBase + PPP_DELTAS.half_board) / 10) * 10,
          full_board: Math.round((pppBase + PPP_DELTAS.full_board) / 10) * 10,
        };
  const pppValid =
    pppPlans ? Object.values(pppPlans).filter((v) => v != null && !Number.isNaN(v)) : [];
  const pppFromMAD = pppValid.length ? Math.min(...pppValid) : null;
  const pppCheapestKey = pppPlans
    ? Object.entries(pppPlans).reduce(
        (acc, [k, v]) => (v != null && (acc === null || v < pppPlans[acc]) ? k : acc),
        null
      )
    : null;

  return {
    publicMAD: fromMAD,           // legacy shape consumed by formatPriceLine + room-rates strip
    unit: "per_night",
    plans,                        // { room_only, breakfast, half_board, full_board } — MAD per night PER ROOM
    fromMAD,
    // ── New per-person surface ──
    perPerson: {
      occupancy,
      plans: pppPlans,            // MAD per night PER PERSON
      fromMAD: pppFromMAD,
      cheapestKey: pppCheapestKey,
    },
  };
};

// Public service price (MAD). Matches title-only first to avoid keyword confounds
// in the description (e.g. a pool desc mentioning a pool bar).
export const getServicePricing = (service, hotelSlug) => {
  const titleOnly = pickName(service?.title);
  const fullLabel = titleOnly + " " + pickName(service?.desc);
  const found = lookup(titleOnly, SERVICE_BASE_MAD) || lookup(fullLabel, SERVICE_BASE_MAD);
  if (!found) return { publicMAD: null, unit: "on_request" };
  if (found.mad === 0) return { publicMAD: 0, unit: found.unit };
  const tier = HOTEL_TIER[hotelSlug] ?? 1.0;
  const mad = Math.round((found.mad * tier) / 10) * 10; // round to nearest 10 MAD
  return { publicMAD: mad, unit: found.unit };
};

// `formatPriceLine` accepts an optional `formatAmount` callback so the
// caller can plug in the active-currency formatter (e.g. `fromMAD` from
// `useFormatPrice()`). When omitted, it falls back to the legacy MAD
// formatter for backwards compatibility.
export const formatPriceLine = (pricing, lang, formatAmount = formatMADAmount) => {
  if (!pricing) return null;
  if (pricing.publicMAD === null) return null;
  const unit = UNIT_LABELS[pricing.unit];
  const unitLabel = unit ? (unit[lang] || unit.en) : "";
  if (pricing.publicMAD === 0) {
    return { amount: null, unit: unitLabel, isIncluded: true };
  }
  return {
    amount: formatAmount(pricing.publicMAD),
    unit: unitLabel,
    isIncluded: false,
  };
};
